import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, Lock, ShoppingCart } from 'lucide-react';
import { formatWithUnit, type PurchaseOrder } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListSkeleton,
  cn,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  usePurchaseOrders,
  useReorderSuggestions,
  useSuppliers,
} from '../lib/purchasing/use-purchasing.js';

/**
 * Buying: what is on order, what needs ordering, and who to ring.
 *
 * The reorder list is at the TOP, above the outstanding orders, because it is the only part of
 * this screen that asks somebody to do something. Outstanding orders are a status check; "you
 * are down to four metres of the silk three brides have chosen" is a task.
 *
 * An item already on an open order is listed with that said rather than filtered out — because
 * "I ordered that on Tuesday" is a thing the person reading this needs to be told.
 */
export function PurchasingPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();

  // Every line of this carries a cost price, so a karigar is refused all of it.
  const allowed = role !== 'tailor';
  const reorder = useReorderSuggestions({ enabled: allowed });
  const orders = usePurchaseOrders(true, { enabled: allowed });
  const suppliers = useSuppliers({ enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('purchasing.title')} />
        <EmptyState
          icon={Lock}
          title={t('purchasing.noAccessTitle')}
          description={t('purchasing.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('purchasing.title')} description={t('purchasing.description')} />

      <div className="flex flex-col gap-6">
        {reorder.isPending ? <ListSkeleton rows={3} /> : null}

        {reorder.data && reorder.data.items.length > 0 ? (
          <Card className="border-warning/40">
            <CardHeader>
              <CardTitle>{t('purchasing.toOrder')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <ul className="flex flex-col gap-1.5">
                {reorder.data.items.map((item) => (
                  <li
                    key={item.stockItemId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/stock/${item.stockItemId}`}
                          className="font-medium underline underline-offset-2"
                        >
                          {item.itemName}
                        </Link>
                        {item.alreadyOnOrder ? (
                          <Badge variant="outline">{t('purchasing.alreadyOnOrder')}</Badge>
                        ) : null}
                      </span>
                      <span className="block text-muted-foreground">
                        {t('purchasing.haveNow', {
                          quantity: formatWithUnit(item.quantity, item.unit),
                        })}
                        {item.supplierName ? ` · ${item.supplierName}` : ''}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block font-semibold tabular-nums">
                        {formatWithUnit(item.suggestedQuantity, item.unit)}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t('purchasing.suggested')}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground" role="status">
                {t('purchasing.estimatedCost', {
                  amount: formatCurrency(reorder.data.estimatedCostPaise),
                })}
              </p>
            </CardContent>
          </Card>
        ) : null}

        <section className="flex flex-col gap-2" aria-label={t('purchasing.outstanding')}>
          <h2 className="text-sm font-semibold">{t('purchasing.outstanding')}</h2>
          {orders.data && orders.data.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {orders.data.map((order) => (
                <li key={order.id}>
                  <OrderRow order={order} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={ShoppingCart}
              title={t('purchasing.noOrdersTitle')}
              description={t('purchasing.noOrdersDescription')}
            />
          )}
        </section>

        <Card>
          <CardHeader>
            <CardTitle>{t('purchasing.suppliers')}</CardTitle>
          </CardHeader>
          <CardContent>
            {suppliers.data && suppliers.data.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {suppliers.data.map((supplier) => (
                  <li
                    key={supplier.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{supplier.name}</span>
                      <span className="block text-muted-foreground">
                        {supplier.contactPerson ? `${supplier.contactPerson} · ` : ''}
                        {supplier.paymentTerms ?? t('purchasing.noTerms')}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block font-semibold tabular-nums">
                        {formatCurrency(supplier.outstandingPaise)}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {t('purchasing.owed')}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('purchasing.noSuppliers')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function OrderRow({ order }: { order: PurchaseOrder }): ReactElement {
  const { t } = useTranslation();
  const short = order.lines.reduce((sum, line) => sum + line.shortQuantity, 0);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm',
        order.overdue ? 'border-destructive/40 bg-destructive/5' : 'border-border',
      )}
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{order.supplierName}</span>
          <Badge variant="outline">{t(`purchasing.status.${order.status}`)}</Badge>
          {order.overdue ? (
            <Badge variant="destructive">
              <AlertTriangle aria-hidden="true" className="size-3" />
              {t('purchasing.overdue')}
            </Badge>
          ) : null}
        </span>
        <span className="block text-muted-foreground">
          {order.number} · {t('purchasing.lineCount', { count: order.lines.length })}
          {order.expectedOn
            ? ` · ${t('purchasing.expected', { date: formatDate(order.expectedOn) })}`
            : ''}
        </span>
        {short > 0 ? (
          // What was ordered is never rewritten, so the gap stays visible until it is closed.
          <span className="block text-xs text-muted-foreground">
            {t('purchasing.stillDue', {
              quantity: formatWithUnit(short, order.lines[0]?.unit ?? 'metre'),
            })}
          </span>
        ) : null}
      </span>
      <span className="text-right">
        <span className="block font-semibold tabular-nums">{formatCurrency(order.totalPaise)}</span>
        {order.receivedPaise > 0 && order.receivedPaise < order.totalPaise ? (
          <span className="block text-xs text-muted-foreground tabular-nums">
            {t('purchasing.receivedSoFar', { amount: formatCurrency(order.receivedPaise) })}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export default PurchasingPage;
